import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import { useSwipeable } from "react-swipeable";
import { ChevronRight, ChevronLeft, CallReceived, CallMade, ShoppingCart, Favorite } from "@mui/icons-material";
import { Link } from "react-router-dom";
import {
  getCartDetails,
  addToCart,
  addToCartGuest,
  addToWishlist,
  getAuthToken,
} from "../services/apiCalls";
import { Container } from "../common/Spacing";
import ProductCard from "./ProductCard";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../context/SnackbarContext";
import { addItemToCart } from "../features/cart/cartThunks";
import { useDispatch } from "react-redux";
import useAppSelector from "../hooks/useAppSelector";
import {
  addWishlistItem,
  removeWishlistItem,
} from "../features/wishlist/wishlistThunks";

const BestCategory = ({ title, subTitle, filteredProducts, products }) => {
  const scrollContainerRef = useRef(null);
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const navigate = useNavigate();
  const [addingToCartId, setAddingToCartId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isInWishlist, setIsInWishlist] = useState({});
  const [isHovered, setIsHovered] = useState(false);
  const { showSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const { items: wishlistItems, loading: wishlistLoading } = useAppSelector(
    (state) => state.wishlist
  );
  const { items: cartItems, loading: cartLoading } = useAppSelector(
    (state) => state.cart
  );

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
      }
    },
    onSwipedRight: () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const handleAddToCart = async (product) => {
    try {
      // Check stock
      if (product.total_stock <= 0) {
        showSnackbar("Sorry, this item is out of stock", "error");
        return;
      }

      // Check for variations
      if (product.product_variation_combination?.length > 0) {
        navigate(`/product/${product.id}`);
        return;
      }

      setAddingToCartId(product.id);

      const cartResponse = await getCartDetails();
      const cartItems = cartResponse.data || [];

      const existingItem = cartItems.find(
        (item) => item.product.id === product.id
      );

      if (existingItem) {
        showSnackbar(
          "Item already in cart. Please update quantity in cart.",
          "error"
        );
        return;
      }

      // Add new item to cart
      const cartItem = {
        product_id: product.id,
        discount: product.discount || "",
        quantity: "1",
        line_discount_type: "percentage",
        unit_price: product.sales_price.toString(),
      };

      await dispatch(addItemToCart(cartItem)).unwrap();
      showSnackbar("Added to cart successfully", "success");
    } catch (error) {
      console.error("Failed to add to cart:", error);

      showSnackbar("Failed to add to cart", "error");
    } finally {
      setAddingToCartId(null);
    }
  };

  const handleWishlistAction = async (product) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      navigate("/signin");
      return;
    }

    try {
      if (isInWishlist) {
        const wishlistItem = wishlistItems.find(
          (item) => item.id === product.id
        );
        await dispatch(removeWishlistItem(wishlistItem.wishlist_id)).unwrap();
      } else {
        await dispatch(addWishlistItem(product.id)).unwrap();
      }

      showSnackbar(
        isInWishlist ? "Removed from wishlist" : "Added to wishlist",
        "success"
      );
    } catch (error) {
      showSnackbar(error?.message || "Failed to update wishlist", "error");
    }
  };

  const handleAddToWishlist = async (product) => {
    const userStr = localStorage.getItem("user");
    if (!userStr) {
      navigate("/signin");
      return;
    }

    if (isInWishlist[product.id]) {
      showSnackbar("This product is already in your wishlist!", "info");
      return;
    }

    try {
      await addToWishlist(product.id);
      setIsInWishlist((prev) => ({
        ...prev,
        [product.id]: true,
      }));
      showSnackbar("Added to wishlist successfully", "success");
    } catch (error) {
      showSnackbar("Failed to add to wishlist", "error");
    }
  };

  return (
    <Container>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {/* Title and Subtitle */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: "8px",
                height: "8px",
                backgroundColor: "#9E6747",
                transform: "rotate(45deg)",
              }}
            />
            <Typography
              variant="p"
              fontSize={"12px"}
              color={"#9E6747"}
              sx={{
                fontFamily: "'Nunito Sans', sans-serif",
                textTransform: "uppercase",
                letterSpacing: "2px",
              }}
            >
              {subTitle}
            </Typography>
          </Box>
          <Typography
            onClick={() =>
              navigate("/custom-products", {
                state: { title, products: products },
              })
            }
            variant="h4"
            fontWeight={"600"}
            component="h2"
            gutterBottom
            width={"fit-content"}
            sx={{
              cursor: "pointer",
              transition: "color 0.3s ease",
              fontSize: { xs: "24px", sm: "32px", md: "40px" },
              "&:hover": {
                color: "#9E6747",
              },
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Arrow Buttons */}
        <Box sx={{ display: "flex", gap: 3 }}>
          <IconButton
            onClick={() =>
              scrollContainerRef.current?.scrollBy({ left: -200, behavior: "smooth" })
            }
            sx={{
              border: "1px solid #000",
              borderRadius: 0,
              width: "40px",
              height: "40px",
              cursor: "pointer",
              color: "#000",
              transform: "rotate(45deg)",
              backgroundColor: "transparent",
              // boxShadow: 2,
              "&:hover": { border: "1px solid #9E6747", color: "#9E6747" },
            }}
          >
            <CallReceived />
          </IconButton>
          <IconButton
            onClick={() =>
              scrollContainerRef.current?.scrollBy({ left: 200, behavior: "smooth" })
            }
            sx={{
              border: "1px solid #000",
              borderRadius: 0,
              width: "40px",
              height: "40px",
              cursor: "pointer",
              color: "#000",
              transform: "rotate(45deg)",
              backgroundColor: "transparent",
              // boxShadow: 2,
              "&:hover": { border: "1px solid #9E6747", color: "#9E6747" },
            }}
          >
            <CallMade />
          </IconButton>
        </Box>
      </Box>

      {/* Product List Section */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          overflow: "hidden",
          whiteSpace: "nowrap",
          gap: 3,
          padding: 2,
          scrollBehavior: "smooth",
          paddingBottom: "50px",
        }}
      >
        {filteredProducts.length > 0 && filteredProducts !== null ? (
          filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              hoveredProductId={hoveredProductId}
              setHoveredProductId={setHoveredProductId}
              handleAddToCart={handleAddToCart}
              handleAddToWishlist={handleWishlistAction}
              addingToCartId={addingToCartId}
              isInWishlist={isInWishlist}
              isHovered={isHovered}
            />
          ))
        ) : (
          <Typography variant="body1">No products available.</Typography>
        )}
      </Box>

      {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: "8px",
            height: "8px",
            backgroundColor: "#9E6747",
            transform: "rotate(45deg)", // Makes it a rhombus
          }}
        />
        <Typography
          variant="p"
          fontSize={"12px"}
          color={"#9E6747"}
          sx={{
            fontFamily: "'Nunito Sans', sans-serif", // Font family
            textTransform: "uppercase",
            // position: "relative",
            // "&::after": {
            //   content: '""',
            //   position: "absolute",
            //   top: "50%",
            //   marginLeft: "1em",
            //   transform: "translateY(-50%)",
            //   width: "150px",
            //   height: "2px",
            //   backgroundColor: "#9E6747",
            // },
          }}
        >
          {subTitle}
        </Typography>
      </Box> */}
      {/* <Typography
        onClick={() =>
          navigate("/custom-products", {
            state: { title, products: products },
          })
        }
        variant="h4"
        fontWeight={"600"}
        component="h2"
        gutterBottom
        width={"fit-content"}
        sx={{
          cursor: "pointer",
          transition: "color 0.3s ease",
          fontSize: { xs: "24px", sm: "32px", md: "40px" },
          "&:hover": {
            color: "#9E6747",
          },
        }}
      >
        {title}
      </Typography> */}
      {/* <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
        <IconButton
          onClick={() => scrollContainerRef.current?.scrollBy({ left: -200, behavior: "smooth" })}
          sx={{
            position: "absolute",
            left: 0,
            zIndex: 1,
            borderRadius: 0,
            width: "40px",
            height: "40px",
            cursor: "pointer",
            color: "#ffffff",
            backgroundColor: "#9E6747",
            boxShadow: 2,
            "&:hover": { backgroundColor: "#000" },
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Box
          ref={scrollContainerRef}
          sx={{
            display: "flex",
            overflow: "hidden",
            whiteSpace: "nowrap",
            gap: 3,
            padding: 2,
            scrollBehavior: "smooth",
            paddingBottom: "50px",
          }}
        >
          {filteredProducts.length > 0 && filteredProducts !== null ? (
            filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                hoveredProductId={hoveredProductId}
                setHoveredProductId={setHoveredProductId}
                handleAddToCart={handleAddToCart}
                handleAddToWishlist={handleWishlistAction}
                addingToCartId={addingToCartId}
                isInWishlist={isInWishlist}
                isHovered={isHovered}
              />
            ))
          ) : (
            <Typography variant="body1">No products available.</Typography>
          )}
        </Box>
        <IconButton
          onClick={() => scrollContainerRef.current?.scrollBy({ left: 200, behavior: "smooth" })}
          sx={{
            position: "absolute",
            right: 0,
            zIndex: 1,
            borderRadius: 0,
            width: "40px",
            height: "40px",
            cursor: "pointer",
            color: "#ffffff",
            backgroundColor: "#9E6747",
            boxShadow: 2,
            "&:hover": { backgroundColor: "#000" },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box> */}
      {/* <Box
        {...handlers}
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          // overflowX: "auto",
          overflow: "hidden",
          whiteSpace: "nowrap",
          gap: 3,
          padding: 2,
          scrollBehavior: "smooth",
          paddingBottom: "50px",
          // "&::-webkit-scrollbar": {
          //   height: "8px",
          // },
          // "&::-webkit-scrollbar-thumb": {
          //   backgroundColor: "#9E6747",
          //   borderRadius: "10px",
          // },
          // "&::-webkit-scrollbar-track": {
          //   backgroundColor: "#ccc",
          // },
        }}
      >
        {filteredProducts.length > 0 && filteredProducts !== null ? (
          filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              hoveredProductId={hoveredProductId}
              setHoveredProductId={setHoveredProductId}
              handleAddToCart={handleAddToCart}
              handleAddToWishlist={handleWishlistAction}
              addingToCartId={addingToCartId}
              isInWishlist={isInWishlist}
              isHovered={isHovered}
            />
          ))
        ) : (
          <Typography variant="body1">No products available.</Typography>
        )}
      </Box> */}
    </Container>
  );
};

export default BestCategory;